import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '@/i18n/LanguageContext';

const Subscribe = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const { t } = useLanguage();

  const handleSubscribe = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('create-checkout-session', { body: {} });
      if (error) throw error;
      if (data.url) window.open(data.url, '_blank');
    } catch (error: any) {
      console.error('Subscription error:', error);
      toast.error(t('subscribe.error'));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-secondary/20 to-accent/20 p-4">
      <div className="container mx-auto max-w-4xl py-8">
        <Button variant="ghost" onClick={() => navigate('/account')} className="mb-6">
          <ArrowLeft className="mr-2 h-4 w-4" />{t('subscribe.backToAccount')}
        </Button>
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold mb-4">{t('subscribe.chooseYourPlan')}</h1>
          <p className="text-muted-foreground">{t('subscribe.unlockUnlimited')}</p>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          <Card className="border-2">
            <CardHeader>
              <CardTitle>{t('subscribe.trial')}</CardTitle>
              <CardDescription>{t('subscribe.getStartedFree')}</CardDescription>
              <div className="mt-4"><span className="text-3xl font-bold">{t('subscribe.free')}</span><span className="text-muted-foreground">{t('subscribe.sevenDays')}</span></div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <div className="flex items-center gap-2"><Check className="h-4 w-4 text-primary" /><span className="text-sm">{t('subscribe.tenRequests')}</span></div>
                <div className="flex items-center gap-2"><Check className="h-4 w-4 text-primary" /><span className="text-sm">{t('subscribe.textAndImage')}</span></div>
                <div className="flex items-center gap-2"><Check className="h-4 w-4 text-primary" /><span className="text-sm">{t('subscribe.seventeenLangs')}</span></div>
                <div className="flex items-center gap-2"><Check className="h-4 w-4 text-primary" /><span className="text-sm">{t('subscribe.sevenDayTrial')}</span></div>
              </div>
              <Button variant="outline" disabled className="w-full">{t('subscribe.currentPlan')}</Button>
            </CardContent>
          </Card>

          <Card className="border-2 border-primary shadow-lg relative overflow-hidden">
            <div className="absolute top-0 right-0 bg-primary text-primary-foreground px-4 py-1 text-sm font-medium">{t('subscribe.popular')}</div>
            <CardHeader>
              <CardTitle>{t('subscribe.premium')}</CardTitle>
              <CardDescription>{t('subscribe.fullAccess')}</CardDescription>
              <div className="mt-4"><span className="text-3xl font-bold">{t('subscribe.price')}</span><span className="text-muted-foreground">{t('subscribe.perMonth')}</span></div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <div className="flex items-center gap-2"><Check className="h-4 w-4 text-primary" /><span className="text-sm font-medium">{t('subscribe.unlimitedTranslations')}</span></div>
                <div className="flex items-center gap-2"><Check className="h-4 w-4 text-primary" /><span className="text-sm">{t('subscribe.textAndImage')}</span></div>
                <div className="flex items-center gap-2"><Check className="h-4 w-4 text-primary" /><span className="text-sm">{t('subscribe.seventeenLangs')}</span></div>
                <div className="flex items-center gap-2"><Check className="h-4 w-4 text-primary" /><span className="text-sm">{t('subscribe.translationHistory')}</span></div>
                <div className="flex items-center gap-2"><Check className="h-4 w-4 text-primary" /><span className="text-sm">{t('subscribe.prioritySupport')}</span></div>
                <div className="flex items-center gap-2"><Check className="h-4 w-4 text-primary" /><span className="text-sm">{t('subscribe.noAds')}</span></div>
              </div>
              <Button onClick={handleSubscribe} disabled={loading} className="w-full" size="lg">
                {loading ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />{t('subscribe.processing')}</>) : t('subscribe.subscribeNow')}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Subscribe;
